import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { 
  Calendar as CalendarIcon, Clock, Video, Users, 
  Phone, MapPin, CheckCircle, Loader2, MessageCircle, ExternalLink
} from "lucide-react";

const SESSION_ICONS = {
  oncall: Phone,
  group: Users,
  retreat: MapPin,
  workshop: Video,
  default: CalendarIcon
};

const SESSION_COLORS = {
  oncall: { bg: 'bg-red-100', text: 'text-red-600' },
  group: { bg: 'bg-blue-100', text: 'text-blue-600' },
  retreat: { bg: 'bg-green-100', text: 'text-green-600' },
  workshop: { bg: 'bg-spa-sage/20', text: 'text-spa-moss' }
};

export default function Sessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [sessionLinks, setSessionLinks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSession, setSelectedSession] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await apiClient.get('/sessions/available');
      setSessions(response.data);
    } catch (error) {
      // silent
    }
  }, []);

  const fetchMyBookings = useCallback(async () => {
    try {
      const response = await apiClient.get('/sessions/my-bookings');
      setMyBookings(response.data);
    } catch (error) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchMyBookings();
    const fetchLinks = async () => {
      try {
        const res = await apiClient.get("/session-links/active");
        setSessionLinks(res.data.links || []);
      } catch (e) { /* silent */ }
    };
    fetchLinks();
  }, [fetchSessions, fetchMyBookings]);

  const handleBook = async (session) => {
    setIsBooking(true);
    try {
      await apiClient.post('/sessions/book', {
        session_type: session.type,
        session_title: session.title,
        scheduled_date: selectedDate.toISOString(),
        duration_minutes: session.duration_minutes,
        price: session.price
      });
      
      setBookingSuccess(true);
      setTimeout(() => {
        setBookingSuccess(false);
        setSelectedSession(null);
      }, 2000);
      
      fetchMyBookings();
    } catch (error) {
      // silent
    } finally {
      setIsBooking(false);
    }
  };

  const getSessionIcon = (type) => SESSION_ICONS[type] || SESSION_ICONS.default;
  const getSessionColors = (type) => SESSION_COLORS[type] || SESSION_COLORS.workshop;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="sessions-page">
        {/* Header */}
        <div>
          <p className="overline-text mb-2">Book Your Session</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            RAPsoul.R OnCALL+ Sessions
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Schedule live sessions with Mr. RAPsoul.R, The Laughing Drama Therapist. 
            Experience personalized wellness coaching, group sessions, and transformative retreats.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sessions List */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="font-serif text-xl text-spa-moss">Available Sessions</h2>
            
            <div className="space-y-4">
              {sessions.map((session) => {
                const Icon = getSessionIcon(session.type);
                const colors = getSessionColors(session.type);
                return (
                  <div 
                    key={session.id}
                    className={`spa-card cursor-pointer transition-all ${
                      selectedSession?.id === session.id ? 'ring-2 ring-spa-terracotta' : ''
                    }`}
                    onClick={() => setSelectedSession(session)}
                    data-testid={`session-${session.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-serif text-lg text-spa-moss">{session.title}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-spa-muted text-spa-text-secondary capitalize">
                            {session.type}
                          </span>
                        </div>
                        <p className="text-sm text-spa-text-secondary mb-3">{session.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-spa-text-secondary">
                            <Clock className="w-4 h-4" />
                            {session.duration_minutes} min
                          </span>
                          <span className="font-medium text-spa-terracotta">
                            CA${session.price.toFixed(2)}
                          </span>
                        </div>
                        <div className="mt-3">
                          <p className="text-xs text-spa-text-secondary">Available slots:</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {session.available_slots.map((slot) => (
                              <span 
                                key={slot}
                                className="text-xs px-2 py-1 bg-spa-muted rounded-full text-spa-moss"
                              >
                                {slot}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="space-y-6">
            {/* Calendar */}
            <div className="spa-card" data-testid="calendar-card">
              <h3 className="font-serif text-lg text-spa-moss mb-4">Select Date</h3>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-xl"
                disabled={(date) => date < new Date()}
              />
            </div>

            {/* Selected Session Summary */}
            {selectedSession && (
              <div className="spa-card bg-spa-sage/10" data-testid="booking-summary">
                <h3 className="font-serif text-lg text-spa-moss mb-4">Booking Summary</h3>
                
                {bookingSuccess ? (
                  <div className="text-center py-6">
                    <CheckCircle className="w-12 h-12 text-spa-sage mx-auto mb-3" />
                    <p className="font-medium text-spa-moss">Session Booked!</p>
                    <p className="text-sm text-spa-text-secondary">Check your email for confirmation</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-spa-text-secondary">Session</span>
                        <span className="text-spa-moss font-medium">{selectedSession.title}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-spa-text-secondary">Date</span>
                        <span className="text-spa-moss">{selectedDate.toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-spa-text-secondary">Duration</span>
                        <span className="text-spa-moss">{selectedSession.duration_minutes} min</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-spa-border pt-3">
                        <span className="text-spa-moss font-medium">Total</span>
                        <span className="text-spa-terracotta font-medium">CA${selectedSession.price.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => handleBook(selectedSession)}
                      disabled={isBooking}
                      className="w-full spa-button-primary"
                      data-testid="book-session-button"
                    >
                      {isBooking ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Booking...
                        </>
                      ) : (
                        'Book Session'
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* My Bookings */}
            <div className="spa-card" data-testid="my-bookings">
              <h3 className="font-serif text-lg text-spa-moss mb-4">My Bookings</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {myBookings.map((booking) => (
                  <div 
                    key={booking.booking_id}
                    className="p-3 bg-spa-muted rounded-xl"
                  >
                    <p className="font-medium text-spa-moss text-sm">{booking.session_title}</p>
                    <p className="text-xs text-spa-text-secondary">
                      {new Date(booking.scheduled_date).toLocaleDateString()}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full inline-block mt-1 ${
                      booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      booking.status === 'completed' ? 'bg-spa-sage/20 text-spa-moss' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                ))}
                {myBookings.length === 0 && (
                  <p className="text-sm text-spa-text-secondary text-center py-4">
                    No bookings yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Zoom & WhatsApp Session Links */}
        {sessionLinks.length > 0 && (
          <div data-testid="session-links-section">
            <h2 className="font-serif text-xl text-spa-moss mb-4">Connect with Your Clinician</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sessionLinks.map(link => (
                <div key={link.link_id} className="spa-card" data-testid={`link-${link.link_id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${link.link_type === "zoom" ? "bg-blue-100" : "bg-green-100"}`}>
                      {link.link_type === "zoom" ? <Video className="w-5 h-5 text-blue-600" /> : <MessageCircle className="w-5 h-5 text-green-600" />}
                    </div>
                    <div>
                      <h3 className="font-medium text-spa-moss text-sm">{link.title}</h3>
                      <p className="text-xs text-spa-text-secondary">{link.description}</p>
                    </div>
                  </div>
                  {link.scheduled_date && (
                    <p className="text-xs text-spa-text-secondary mb-3 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {link.scheduled_date} {link.scheduled_time && `at ${link.scheduled_time}`}
                    </p>
                  )}
                  {link.link_type === "zoom" && link.url && (
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full text-sm hover:bg-blue-700 transition-colors" data-testid={`join-zoom-${link.link_id}`}>
                      <Video className="w-4 h-4" /> Join Zoom Meeting
                    </a>
                  )}
                  {link.link_type === "whatsapp" && link.whatsapp_number && (
                    <a href={`https://wa.me/${link.whatsapp_number}${link.whatsapp_message ? `?text=${encodeURIComponent(link.whatsapp_message)}` : ""}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full text-sm hover:bg-green-700 transition-colors" data-testid={`chat-whatsapp-${link.link_id}`}>
                      <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
